import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { OriginalsConfig, ParamValue } from '../types.js';
import type { BetLimits } from './AmountField.js';
import { ModeTabs, type BetMode } from './ModeTabs.js';
import { AmountField } from './AmountField.js';
import { ParamControls } from './ParamControls.js';
import { BetButton } from './BetButton.js';
import { BetNote } from './BetNote.js';
import { AutoControls } from './auto/AutoControls.js';
import { useAutoBet, type AutoConfig, type RoundResult } from './auto/useAutoBet.js';

const DEFAULT_AUTO: AutoConfig = {
  numberOfBets: 0,
  onWin: { mode: 'reset', pct: 0 },
  onLoss: { mode: 'reset', pct: 0 },
  stopProfit: null,
  stopLoss: null,
};

interface Props {
  config: OriginalsConfig;
  limits: BetLimits;
  bet: bigint;
  onBet: (v: bigint) => void;
  params: Record<string, ParamValue>;
  onParam: (key: string, value: ParamValue) => void;
  balance: bigint | null;
  busy: boolean;
  error: string | null;
  /** Tags the current error so the panel can offer a relogin CTA on an expired session. */
  errorKind?: 'insufficient' | 'invalid-selection' | 'session-expired' | 'generic' | null;
  /** Invoked when the player taps the relogin CTA after a session-expired error. */
  onRelogin?: () => void;
  /** Performs one bet and resolves after the scene reveal. */
  playRound: (bet: bigint, selection?: import('../scenes/types.js').SceneSelection) => Promise<RoundResult>;
  supportsAuto: boolean;
  /** Pre-bet selection validity (keno picks.length>=1, dice bet chosen); undefined → always valid. */
  selectionValid?: boolean;
  /** Current selection, snapshotted by auto-bet at start. */
  selection?: import('../scenes/types.js').SceneSelection;
  /** Fixed bet options (scratch) → AmountField renders chips instead of a stepper. */
  options?: bigint[] | null;
  /** Notifies the page when auto-bet starts/stops (drives the scene turbo hint). */
  onAutoRunningChange?: (running: boolean) => void;
  /**
   * When true, suppresses the primary Bet/Start CTA, the manual/auto mode toggle, and the auto
   * controls. Use for stateful games (e.g. Mines) where the CTA lives inside the game view itself.
   * Amount field and param controls still render. Defaults to false.
   */
  hideCta?: boolean;
  /**
   * Set when nobody is signed in. Every bet CTA (manual and auto) calls this instead
   * of playing, so a visitor sees the game and is asked to sign in at the moment they
   * would stake money — no play request is ever made for them.
   */
  onGuestPlay?: () => void;
  /** Press-and-hold on the manual CTA fires repeated bets (Plinko rapid drop). */
  holdToRepeat?: boolean;
  /** One-line hint rendered under the manual CTA (e.g. Plinko's "hold to drop"). */
  ctaHint?: string;
  /**
   * Selection board (e.g. DiceBoard) rendered directly under the CTA on mobile
   * only — keeps the bet button at the top of the controls, above the bet-type
   * picker. On desktop the board lives in the wide game stage, so this is hidden
   * (lg:hidden) to avoid a double render.
   */
  selectionSlot?: import('react').ReactNode;
}

export function BetPanel(props: Props) {
  const { t } = useTranslation('originals');
  const { onAutoRunningChange, hideCta = false } = props;
  const [mode, setMode] = useState<BetMode>('manual');
  const [autoCfg, setAutoCfg] = useState<AutoConfig>(DEFAULT_AUTO);
  const [insufficient, setInsufficient] = useState(false);

  const auto = useAutoBet({
    baseBet: () => props.bet,
    limits: props.limits,
    balance: () => props.balance,
    playRound: props.playRound,
    selection: () => props.selection,
    onInsufficient: () => setInsufficient(true),
  });

  useEffect(() => {
    onAutoRunningChange?.(auto.running);
  }, [auto.running, onAutoRunningChange]);

  const betValid = props.bet >= props.limits.min && props.bet <= props.limits.max;
  const locked = props.busy || auto.running;
  const selectionOk = props.selectionValid ?? true;
  const canBet = betValid && selectionOk;
  // Distinguishes a selectable game with no valid selection (false) from a
  // non-selectable game (undefined → always valid). Drives the inline prompt.
  const needsSelection = props.selectionValid === false;

  const onManualPlay = useCallback(() => {
    // Hard guard: never fire a play without a valid selection / bet (closes the ORIG-008 path).
    if (!canBet) return;
    // A rejected playRound (401/expired session, 4xx, network error) must NEVER escape as an
    // uncaught promise rejection — that surfaces as a React error overlay and hangs the page.
    // The rejection reason is already mapped to `props.error` by useOriginalsPlay and rendered
    // in the error slot below; the hook also returns the panel to an idle, re-bettable state.
    // Swallow it here so the manual path stays as safe as the auto loop.
    props.playRound(props.bet, props.selection).catch(() => {});
  }, [canBet, props]);

  const guestPlay = props.onGuestPlay;

  const onAutoToggle = useCallback(() => {
    setInsufficient(false);
    if (auto.running) auto.stop();
    else if (canBet) auto.start(autoCfg);
  }, [auto, autoCfg, canBet]);

  return (
    <div className="flex flex-col gap-3.5">
      {!hideCta && props.supportsAuto && <ModeTabs mode={mode} onChange={setMode} disabled={locked} />}

      <AmountField
        value={props.bet}
        limits={props.limits}
        balance={props.balance}
        disabled={locked}
        options={props.options}
        onChange={props.onBet}
      />

      <ParamControls schema={props.config.params} values={props.params} onChange={props.onParam} disabled={locked} />

      {!hideCta && mode === 'auto' && props.supportsAuto && (
        <AutoControls value={autoCfg} disabled={auto.running} onChange={setAutoCfg} />
      )}

      {!hideCta && (props.error || insufficient) && (
        <div className="flex flex-col gap-1.5">
          <span className="text-[12px] text-[var(--danger)]" role="alert">
            {insufficient ? t('panel.insufficientBalance') : props.error}
          </span>
          {props.errorKind === 'session-expired' && props.onRelogin && (
            <button
              type="button"
              onClick={props.onRelogin}
              className="self-start rounded-lg bg-[var(--primary)] px-3 py-1.5 text-[12px] font-semibold text-[var(--primary-on)] transition-colors hover:brightness-110"
            >
              {t('reloginCta')}
            </button>
          )}
        </div>
      )}

      {!hideCta && needsSelection && !props.error && !insufficient && (
        <span className="text-[12px] text-[var(--text-secondary)]" role="status">
          {t('panel.selectFirst')}
        </span>
      )}

      {!hideCta && (
        // ponytail: order-first pins the CTA to the top on mobile (flex-col); lg:order-none restores
        // bottom placement on desktop where the panel is a tall sidebar. Matches the lg breakpoint
        // the parent uses to flip game scene / bet panel.
        <div className="order-first lg:order-none flex flex-col gap-3.5">
          {mode === 'manual' || !props.supportsAuto ? (
            <div className="flex flex-col gap-1.5">
              <BetButton
                label={t('panel.bet')}
                busy={props.busy}
                disabled={guestPlay ? false : locked || !canBet}
                onClick={guestPlay ?? onManualPlay}
                repeatOnHold={!guestPlay && Boolean(props.holdToRepeat) && !props.error && !insufficient}
              />
              {props.ctaHint && !guestPlay && (
                <span className="text-center text-[11px] text-[var(--text-muted)]">{props.ctaHint}</span>
              )}
            </div>
          ) : (
            <BetButton
              label={auto.running ? t('auto.stop') : t('auto.start')}
              danger={auto.running}
              disabled={guestPlay ? false : !canBet && !auto.running}
              onClick={guestPlay ?? onAutoToggle}
            />
          )}
          {guestPlay && (
            <span className="text-center text-[12px] text-[var(--text-secondary)]" role="status">
              {t('guest.signInToPlay')}
            </span>
          )}
          {props.selectionSlot && <div className="lg:hidden">{props.selectionSlot}</div>}
        </div>
      )}

      <BetNote tone={mode === 'auto' ? 'info' : 'info'}>
        {mode === 'auto' ? t('auto.note') : t('panel.demoNote')}
      </BetNote>
    </div>
  );
}
